import { useMemo, useState } from 'react';
import { Loader2, Package, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SectionHeader } from '@/components/ui/section-header';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { StatusPill } from '@/components/ui/status-pill';
import { SelectField } from '@/components/ui/select';
import { field_errors_from, form_error_message } from '@/lib/forms';
import { cn, format_inr } from '@/lib/utils';
import { use_services } from '@/features/vendor-service/hooks';
import {
  use_create_service_package,
  use_delete_service_package,
  use_service_packages,
  use_update_service_package,
} from '@/features/service-package/hooks';
import {
  PACKAGE_DURATIONS,
  sessions_per_week,
  type CreateServicePackageInput,
  type PackageDuration,
  type PackageModule,
  type ServicePackage,
} from '@/features/service-package/types';

/** Backend cap on sessions in one package, regardless of duration. */
const MAX_SESSIONS = 60;

const PACKAGE_MODULES: readonly string[] = ['WALKER', 'TRAINER'];

type FormState = {
  service_id: string;
  name: string;
  description: string;
  duration_days: PackageDuration;
  sessions_count: string;
  price_inr: string;
  is_active: boolean;
};

const EMPTY: FormState = {
  service_id: '',
  name: '',
  description: '',
  duration_days: 7,
  sessions_count: '',
  price_inr: '',
  is_active: true,
};

function package_to_form(p: ServicePackage): FormState {
  return {
    service_id: p.service_id,
    name: p.name,
    description: p.description ?? '',
    duration_days: (PACKAGE_DURATIONS as readonly number[]).includes(p.duration_days)
      ? (p.duration_days as PackageDuration)
      : 7,
    sessions_count: String(p.sessions_count),
    price_inr: p.price_inr,
    is_active: p.is_active,
  };
}

/** "Walks" for a walker service, "Classes" for a trainer one. */
function session_noun(module: PackageModule | undefined, count: number): string {
  if (module === 'WALKER') return count === 1 ? 'walk' : 'walks';
  if (module === 'TRAINER') return count === 1 ? 'class' : 'classes';
  return count === 1 ? 'session' : 'sessions';
}

export function VendorServicePackagesPage() {
  const packages_q = use_service_packages();
  const services_q = use_services();
  const create_m = use_create_service_package();
  const update_m = use_update_service_package();
  const delete_m = use_delete_service_package();

  const packages = packages_q.data ?? [];
  const eligible_services = useMemo(
    () => (services_q.data ?? []).filter((s) => PACKAGE_MODULES.includes(s.module_type)),
    [services_q.data],
  );

  const [editing, set_editing] = useState<null | { id?: string }>(null);
  const [form, set_form] = useState<FormState>(EMPTY);
  const [error, set_error] = useState<string | null>(null);
  const [field_errors, set_field_errors] = useState<Record<string, string>>({});

  function open_new() {
    set_error(null);
    set_field_errors({});
    set_form({ ...EMPTY, service_id: eligible_services.length === 1 ? eligible_services[0].id : '' });
    set_editing({});
  }
  function open_edit(p: ServicePackage) {
    set_error(null);
    set_field_errors({});
    set_form(package_to_form(p));
    set_editing({ id: p.id });
  }
  function close_form() {
    set_editing(null);
    set_error(null);
    set_field_errors({});
  }

  const editing_pkg = editing?.id ? packages.find((p) => p.id === editing.id) : undefined;
  const chosen_service = eligible_services.find((s) => s.id === form.service_id);
  const chosen_module: PackageModule | undefined =
    (chosen_service?.module_type as PackageModule | undefined) ?? editing_pkg?.module_type;
  const sessions_label = chosen_module === 'WALKER' ? 'Walks' : chosen_module === 'TRAINER' ? 'Classes' : 'Sessions';

  const sessions = Number(form.sessions_count);
  const price = Number(form.price_inr);
  const sessions_valid = Number.isInteger(sessions) && sessions >= 1;
  const sessions_too_many = sessions_valid && (sessions > form.duration_days || sessions > MAX_SESSIONS);
  const preview_per_week =
    sessions_valid && !sessions_too_many
      ? sessions_per_week({ sessions_count: sessions, duration_days: form.duration_days })
      : null;

  const service_options = eligible_services.map((s) => ({
    value: s.id,
    label: `${s.name} (${s.module_type === 'WALKER' ? 'Walking' : 'Training'}${s.duration_minutes ? ` · ${s.duration_minutes} min` : ''})`,
  }));
  // An edited package may point at a service that is no longer in the list (e.g. deactivated) — keep it selectable.
  if (editing_pkg && !service_options.some((o) => o.value === editing_pkg.service_id)) {
    service_options.push({ value: editing_pkg.service_id, label: editing_pkg.service_name });
  }

  const saving = create_m.isPending || update_m.isPending;

  function validate(): CreateServicePackageInput | string {
    if (!form.service_id) return 'Choose the walking or training service this package is for.';
    const name = form.name.trim();
    if (name.length < 2) return 'Give this package a name (at least 2 characters).';
    if (name.length > 120) return 'Keep the name under 120 characters.';
    if (!sessions_valid) return `Enter how many ${sessions_label.toLowerCase()} are included (a whole number, at least 1).`;
    if (sessions > form.duration_days)
      return `A ${form.duration_days}-day package can include at most ${form.duration_days} ${sessions_label.toLowerCase()}.`;
    if (sessions > MAX_SESSIONS) return `A package can include at most ${MAX_SESSIONS} ${sessions_label.toLowerCase()}.`;
    if (!Number.isFinite(price) || price <= 0) return 'Enter a total price greater than 0.';

    return {
      service_id: form.service_id,
      name,
      description: form.description.trim() || undefined,
      duration_days: form.duration_days,
      sessions_count: sessions,
      price_inr: price,
      is_active: form.is_active,
    };
  }

  async function submit() {
    set_error(null);
    set_field_errors({});
    const result = validate();
    if (typeof result === 'string') return set_error(result);
    try {
      if (editing?.id) {
        // service_id is fixed once a package exists; description may be cleared to null.
        const { service_id: _service_id, description, ...rest } = result;
        await update_m.mutateAsync({ id: editing.id, input: { ...rest, description: description ?? null } });
      } else {
        await create_m.mutateAsync(result);
      }
      close_form();
    } catch (err) {
      set_field_errors(field_errors_from(err));
      set_error(form_error_message(err, 'Could not save this package.', { highlights_fields: true }));
    }
  }

  async function toggle_active(p: ServicePackage) {
    try {
      await update_m.mutateAsync({ id: p.id, input: { is_active: !p.is_active } });
    } catch {
      /* surfaced via list refetch; ignore inline */
    }
  }

  async function remove(p: ServicePackage) {
    if (
      !window.confirm(
        `Delete the “${p.name}” package? If customers have already bought it, it will be deactivated instead so their plans keep running.`,
      )
    )
      return;
    await delete_m.mutateAsync(p.id);
  }

  const no_services = !services_q.isLoading && eligible_services.length === 0;

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader
        title="Packages"
        description="Bundle walks or classes over 7, 15 or 30 days at one total price. Customers pick which weekdays they want."
        action={
          !editing && (
            <Button onClick={open_new} variant="gradient" size="sm" disabled={no_services} data-testid="add-package">
              <Plus className="h-4 w-4" /> Add package
            </Button>
          )
        }
      />

      {editing && (
        <Card data-testid="package-form">
          <div className="flex items-center justify-between border-b-2 border-brand-outline-variant px-5 py-3.5">
            <h2 className="font-heading text-base font-bold">{editing.id ? 'Edit package' : 'New package'}</h2>
            <Button variant="ghost" size="icon" onClick={close_form} aria-label="Close" data-testid="package-cancel">
              <X className="h-4 w-4" />
            </Button>
          </div>
          <CardContent className="flex flex-col gap-4 p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="pkg-service">Service</Label>
                <SelectField
                  id="pkg-service"
                  data-testid="pkg-service"
                  value={form.service_id}
                  onValueChange={(v) => set_form((f) => ({ ...f, service_id: v }))}
                  options={service_options}
                  placeholder={services_q.isLoading ? 'Loading services…' : 'Choose a service'}
                  disabled={!!editing.id || services_q.isLoading}
                />
                {editing.id ? (
                  <p className="text-xs text-muted-foreground">The service can’t be changed after a package is created.</p>
                ) : (
                  <p className="text-xs text-muted-foreground">Sets the length of each walk or class.</p>
                )}
                {field_errors.service_id && <p className="text-xs text-destructive">{field_errors.service_id}</p>}
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="pkg-name">Name</Label>
                <Input
                  id="pkg-name"
                  data-testid="pkg-name"
                  value={form.name}
                  maxLength={120}
                  onChange={(e) => set_form((f) => ({ ...f, name: e.target.value }))}
                  placeholder={chosen_module === 'TRAINER' ? 'Puppy basics — 2 weeks' : 'Weekday walks — 1 month'}
                />
                {field_errors.name && <p className="text-xs text-destructive">{field_errors.name}</p>}
              </div>

              <div className="flex flex-col gap-1.5">
                <Label id="pkg-duration-label">Duration</Label>
                <div
                  role="radiogroup"
                  aria-labelledby="pkg-duration-label"
                  className="grid grid-cols-3 gap-1 rounded-input border-2 border-input bg-secondary p-1"
                  data-testid="pkg-duration"
                >
                  {PACKAGE_DURATIONS.map((d) => {
                    const selected = form.duration_days === d;
                    return (
                      <button
                        key={d}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => set_form((f) => ({ ...f, duration_days: d }))}
                        data-testid={`pkg-duration-${d}`}
                        className={cn(
                          'h-9 rounded-input text-sm font-semibold transition-all duration-fast',
                          selected ? 'bg-primary text-primary-foreground shadow-clay' : 'text-muted-foreground hover:text-foreground',
                        )}
                      >
                        {d} days
                      </button>
                    );
                  })}
                </div>
                {field_errors.duration_days && <p className="text-xs text-destructive">{field_errors.duration_days}</p>}
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="pkg-sessions" className="flex items-center gap-1">
                  {sessions_label}{' '}
                  <span className="text-xs font-normal text-muted-foreground">(max {Math.min(form.duration_days, MAX_SESSIONS)})</span>
                </Label>
                <Input
                  id="pkg-sessions"
                  data-testid="pkg-sessions"
                  type="number"
                  min={1}
                  max={Math.min(form.duration_days, MAX_SESSIONS)}
                  step={1}
                  value={form.sessions_count}
                  onChange={(e) => set_form((f) => ({ ...f, sessions_count: e.target.value }))}
                  placeholder={form.duration_days === 7 ? '3' : form.duration_days === 15 ? '6' : '12'}
                  className={field_errors.sessions_count || sessions_too_many ? 'border-destructive' : undefined}
                />
                {field_errors.sessions_count ? (
                  <p className="text-xs text-destructive">{field_errors.sessions_count}</p>
                ) : sessions_too_many ? (
                  <p className="text-xs text-destructive" data-testid="pkg-sessions-error">
                    At most {Math.min(form.duration_days, MAX_SESSIONS)} {sessions_label.toLowerCase()} in {form.duration_days} days.
                  </p>
                ) : preview_per_week != null ? (
                  <p className="text-xs font-semibold text-success" data-testid="pkg-sessions-hint">
                    {sessions} {session_noun(chosen_module, sessions)} · customer picks {preview_per_week}{' '}
                    {preview_per_week === 1 ? 'day' : 'days'} a week
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="pkg-price" className="flex items-center gap-1">
                  Total price <span className="text-xs font-normal text-muted-foreground">(₹, whole package)</span>
                </Label>
                <Input
                  id="pkg-price"
                  data-testid="pkg-price"
                  type="number"
                  min={1}
                  value={form.price_inr}
                  onChange={(e) => set_form((f) => ({ ...f, price_inr: e.target.value }))}
                  placeholder="2100"
                />
                {field_errors.price_inr ? (
                  <p className="text-xs text-destructive">{field_errors.price_inr}</p>
                ) : sessions_valid && !sessions_too_many && Number.isFinite(price) && price > 0 ? (
                  <p className="text-xs text-muted-foreground" data-testid="pkg-price-per-session">
                    ≈ {format_inr(Math.round(price / sessions))} per {session_noun(chosen_module, 1)}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <Label htmlFor="pkg-desc" className="flex items-center gap-1">
                  Description <span className="text-xs font-normal text-muted-foreground">(optional)</span>
                </Label>
                <textarea
                  id="pkg-desc"
                  data-testid="pkg-desc"
                  value={form.description}
                  onChange={(e) => set_form((f) => ({ ...f, description: e.target.value }))}
                  rows={2}
                  className="rounded-input border-2 border-input bg-card px-4 py-3 text-base text-foreground shadow-[inset_0_2px_4px_rgba(40,23,26,0.04)] transition-all duration-fast focus-visible:border-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/15"
                  placeholder="What’s covered, what to expect…"
                />
              </div>

              <label className="flex cursor-pointer items-center justify-between gap-3 rounded-input border-2 border-input bg-secondary px-4 py-2.5 sm:col-span-2">
                <span className="flex flex-col">
                  <span className="text-sm font-semibold">Active</span>
                  <span className="text-xs text-muted-foreground">Visible to customers and purchasable</span>
                </span>
                <input
                  type="checkbox"
                  data-testid="pkg-active"
                  checked={form.is_active}
                  onChange={(e) => set_form((f) => ({ ...f, is_active: e.target.checked }))}
                  className="h-5 w-5 shrink-0 accent-primary"
                />
              </label>
            </div>

            {error && (
              <p className="text-sm text-destructive" data-testid="package-form-error" role="alert">
                {error}
              </p>
            )}

            <div className="flex gap-2">
              <Button onClick={submit} variant="gradient" disabled={saving} data-testid="pkg-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {editing.id ? 'Save changes' : 'Create package'}
              </Button>
              <Button onClick={close_form} variant="ghost">
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {packages_q.isLoading ? (
        <Card>
          <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        </Card>
      ) : packages_q.error ? (
        <ErrorState
          data-testid="packages-error"
          title="Failed to load packages"
          description={form_error_message(packages_q.error, 'Please try again.')}
        />
      ) : packages.length === 0 ? (
        <EmptyState
          data-testid="packages-empty"
          icon={Package}
          title="No packages yet"
          description={
            no_services
              ? 'Add a walking or training service first — each package is built on one of your services.'
              : 'Offer 7, 15 or 30-day bundles of walks or classes at a single price.'
          }
          action={
            no_services ? undefined : (
              <Button onClick={open_new} variant="gradient">
                <Plus className="h-4 w-4" /> Add package
              </Button>
            )
          }
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="packages-list">
          {packages.map((p) => (
            <Card key={p.id} data-testid={`package-${p.id}`}>
              <CardContent className="flex flex-col gap-3 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <StatusPill tone={p.module_type === 'WALKER' ? 'info' : 'success'}>
                      {p.module_type === 'WALKER' ? 'Walking' : 'Training'}
                    </StatusPill>
                    <h3 className="mt-1 font-heading text-base font-bold">{p.name}</h3>
                    <p className="text-xs text-muted-foreground" data-testid={`package-service-${p.id}`}>
                      {p.service_name}
                      {p.session_minutes ? ` · ${p.session_minutes} min each` : ''}
                    </p>
                  </div>
                  {!p.is_active && <StatusPill tone="warning">Inactive</StatusPill>}
                </div>

                {p.description && <p className="text-xs text-muted-foreground">{p.description}</p>}

                <p className="font-heading text-base font-bold tabular-nums" data-testid={`package-summary-${p.id}`}>
                  {p.duration_days} days · {p.sessions_count} {session_noun(p.module_type, p.sessions_count)} ·{' '}
                  {format_inr(p.price_inr)}
                  {/* Strike-off vs booking singly (Nov 2026). */}
                  {p.compare_at_price_inr && (
                    <>
                      {' '}
                      <span className="text-muted-foreground line-through">{format_inr(p.compare_at_price_inr)}</span>{' '}
                      <span className="font-semibold text-success">{p.discount_pct}% off</span>
                    </>
                  )}
                </p>
                <p className="w-fit rounded-full bg-secondary px-2 py-0.5 text-xs" data-testid={`package-per-week-${p.id}`}>
                  {p.sessions_per_week}× a week · customer picks {p.sessions_per_week}{' '}
                  {p.sessions_per_week === 1 ? 'day' : 'days'}
                </p>

                <div className="mt-1 flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => toggle_active(p)} data-testid={`pkg-toggle-${p.id}`}>
                    {p.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => open_edit(p)} data-testid={`pkg-edit-${p.id}`} aria-label="Edit">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(p)} data-testid={`pkg-delete-${p.id}`} aria-label="Delete">
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
